import { useState } from "react";
import { api } from "@/lib/api";
import { Header, Footer } from "@/pages/LandingPage";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PatternDivider } from "@/components/EthnicPatterns";
import { Mail, Check, Send } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(c.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-af">
      <Header />
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-20">
        <div className="text-center mb-12">
          <PatternDivider />
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-4" data-testid="contact-title">
            {c.title}
          </h1>
          <p className="mt-4 text-af-secondary max-w-xl mx-auto">{c.sub}</p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
          {/* Info panel */}
          <div className="bg-[#2B1F12] text-white rounded-md p-8">
            <div className="h-12 w-12 rounded-md bg-white/10 border border-white/15 flex items-center justify-center mb-5">
              <Mail className="h-6 w-6 text-[#D4A04E]" />
            </div>
            <h2 className="font-display font-bold text-xl mb-3">{c.infoTitle}</h2>
            <p className="text-sm text-white/75 leading-relaxed mb-6">{c.infoText}</p>
            <ul className="space-y-3">
              {c.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-white/85">
                  <Check className="h-4 w-4 text-[#D4A04E] shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="bg-white border border-af rounded-md p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="c-name">{c.name} *</Label>
                <Input id="c-name" required value={form.name} onChange={set("name")}
                  data-testid="contact-name-input"
                  className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder={c.namePh} />
              </div>
              <div>
                <Label htmlFor="c-email">{c.email} *</Label>
                <Input id="c-email" type="email" required value={form.email} onChange={set("email")}
                  data-testid="contact-email-input"
                  className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder={c.emailPh} />
              </div>
            </div>
            <div>
              <Label htmlFor="c-subject">{c.subject}</Label>
              <Input id="c-subject" value={form.subject} onChange={set("subject")}
                data-testid="contact-subject-input"
                className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder={c.subjectPh} />
            </div>
            <div>
              <Label htmlFor="c-message">{c.message} *</Label>
              <Textarea id="c-message" required rows={6} value={form.message} onChange={set("message")}
                data-testid="contact-message-input"
                className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder={c.messagePh} />
            </div>
            <Button type="submit" disabled={sending} data-testid="contact-submit-btn"
              className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 px-6 font-semibold">
              <Send className="h-4 w-4 mr-2" />
              {sending ? c.sending : c.send}
            </Button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
